import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Bell, KeyRound, User as UserIcon, Sun, Moon, Monitor } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= 10) score++;
  if (/[A-Za-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 14) score++;
  return Math.min(score, 4);
}

function StrengthMeter({ pw }) {
  const score = passwordStrength(pw);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#E2E8F0", "#DC2626", "#C2410C", "#5EAB45", "#047857"];
  return (
    <div className="mt-1.5">
      <div className="flex h-1.5 gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="flex-1 rounded-full"
            style={{ backgroundColor: i <= score ? colors[score] : "#E2E8F0" }}
          />
        ))}
      </div>
      <p
        className="mt-1 text-[11px] font-medium"
        style={{ color: score >= 1 ? colors[score] : "#94A3B8" }}
      >
        {pw ? labels[score] || "Very weak" : "Use ≥10 chars, with letter, number & symbol"}
      </p>
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.full_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", new: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  if (!user) return null;

  const profileDirty =
    (name && name !== user.full_name) || (email && email.toLowerCase() !== user.email);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const body = {};
      if (name && name.trim() !== (user.full_name || "")) {
        body.full_name = name.trim();
      }
      // Phase 8 fix — only send `email` when the user actually changed it.
      // Avoids hitting the EmailStr/RFC 2606 .test-TLD rejection on
      // unchanged emails (belt-and-suspenders alongside the backend
      // switching to a permissive str + regex validator).
      const trimmedEmail = (email || "").trim().toLowerCase();
      if (trimmedEmail && trimmedEmail !== (user.email || "").toLowerCase()) {
        body.email = trimmedEmail;
      }
      if (Object.keys(body).length === 0) {
        toast.message("Nothing to save", { description: "No fields changed." });
        setSavingProfile(false);
        return;
      }
      await api.patch("/auth/profile", body);
      toast.success("Profile updated");
      await refreshUser?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (pw.new !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.new,
      });
      toast.success("Password updated");
      setPw({ current: "", new: "", confirm: "" });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg =
        status === 401
          ? "Current password is incorrect"
          : typeof detail === "string"
          ? detail
          : "Could not change password";
      toast.error(msg);
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="profile-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-[color:var(--page-fg)]">
          My profile
        </h1>
        <p className="mt-1 text-sm text-[color:var(--page-fg-secondary)]">
          Manage your account, security, and notification preferences.
        </p>
      </div>

      <div className="space-y-6">
        {/* Profile basics */}
        <section className="brand-card p-6" data-testid="profile-section-basics">
          <header className="mb-4 flex items-center gap-3">
            <UserIcon className="h-5 w-5 text-brand-navy" />
            <div>
              <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
                Profile
              </h2>
              <p className="text-xs text-[color:var(--page-fg-secondary)]">
                Your name and contact email.
              </p>
            </div>
          </header>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Full name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="profile-full-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="profile-email"
              />
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                Email change takes effect immediately and is recorded in the audit log.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Role</Label>
              <Input
                value={user.role || ""}
                disabled
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-brand-elevated2"
                data-testid="profile-role"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Account status</Label>
              <div className="flex h-10 items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] bg-brand-elevated2 px-3 text-sm">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#5EAB45" }} />
                Active
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              disabled={!profileDirty || savingProfile}
              onClick={saveProfile}
              className="h-10 rounded-xl px-5 font-semibold text-white"
              style={{ backgroundColor: "#1E3A5F" }}
              data-testid="profile-save"
            >
              {savingProfile ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </section>

        {/* Change password */}
        <section className="brand-card p-6" data-testid="profile-section-password">
          <header className="mb-4 flex items-center gap-3">
            <KeyRound className="h-5 w-5 text-brand-navy" />
            <div>
              <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
                Change password
              </h2>
              <p className="text-xs text-[color:var(--page-fg-secondary)]">
                Verify your current password and choose a new one.
              </p>
            </div>
          </header>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Current password</Label>
              <Input
                type="password"
                value={pw.current}
                onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="profile-current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">New password</Label>
              <Input
                type="password"
                value={pw.new}
                onChange={(e) => setPw((p) => ({ ...p, new: e.target.value }))}
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="profile-new-password"
              />
              <StrengthMeter pw={pw.new} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Confirm new password</Label>
              <Input
                type="password"
                value={pw.confirm}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                className="h-10 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="profile-confirm-password"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              disabled={!pw.current || !pw.new || !pw.confirm || savingPw}
              onClick={savePassword}
              className="h-10 rounded-xl px-5 font-semibold text-white"
              style={{ backgroundColor: "#1E3A5F" }}
              data-testid="profile-save-password"
            >
              {savingPw ? "Updating…" : "Update password"}
            </Button>
          </div>
        </section>

        {/* Sub-link cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Link
            to="/settings/security"
            className="brand-card flex items-center gap-3 p-5 transition-all hover:shadow-brand"
            data-testid="profile-link-security"
          >
            <ShieldCheck className="h-6 w-6 text-brand-navy" />
            <div className="flex-1">
              <p className="font-semibold text-[color:var(--page-fg)]">Security</p>
              <p className="text-xs text-[color:var(--page-fg-secondary)]">
                Multi-factor authentication, sign-in history, backup codes.
              </p>
            </div>
            <span className="text-brand-navy">→</span>
          </Link>
          <Link
            to="/settings/preferences"
            className="brand-card flex items-center gap-3 p-5 transition-all hover:shadow-brand"
            data-testid="profile-link-preferences"
          >
            <Bell className="h-6 w-6 text-brand-navy" />
            <div className="flex-1">
              <p className="font-semibold text-[color:var(--page-fg)]">Notifications</p>
              <p className="text-xs text-[color:var(--page-fg-secondary)]">
                Email alerts and per-channel preferences.
              </p>
            </div>
            <span className="text-brand-navy">→</span>
          </Link>
        </div>

        {/* Phase 11 — Display preferences */}
        <DisplaySection />
      </div>
    </div>
  );
}

function DisplaySection() {
  const { preference, resolved, setPreference, loading } = useTheme();
  const options = [
    {
      value: "light",
      label: "Light",
      hint: "Goodwin Resolve default branding",
      Icon: Sun,
    },
    {
      value: "dark",
      label: "Dark",
      hint: "Deep navy canvas, easier in low light",
      Icon: Moon,
    },
    {
      value: "system",
      label: "System",
      hint: "Follow the OS preference",
      Icon: Monitor,
    },
  ];
  return (
    <section
      data-testid="profile-display-section"
      className="brand-card mt-8 p-6"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-lg font-semibold text-[color:var(--page-fg)]">
            Display
          </h2>
          <p className="mt-0.5 text-xs text-[color:var(--page-fg-muted)]">
            Choose how Goodwin Resolve looks for you. Your preference is saved
            to your profile and synced across devices.{" "}
            <span className="text-[color:var(--page-fg-secondary)]">
              Currently rendering: <b>{resolved}</b>
              {preference === "system" ? " (system-driven)" : ""}.
            </span>
          </p>
        </div>
        {loading && (
          <span className="text-xs text-brand-teal">Saving…</span>
        )}
      </div>
      <div
        role="radiogroup"
        aria-label="Theme preference"
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
      >
        {options.map(({ value, label, hint, Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              role="radio"
              aria-checked={active}
              type="button"
              data-testid={`profile-theme-${value}`}
              onClick={() => setPreference(value)}
              className={
                "flex items-start gap-3 rounded-xl border p-3 text-left transition " +
                (active
                  ? "border-brand-teal bg-[color:var(--surface-2)] shadow-brand-sm dark:border-brand-green dark:bg-brand-green/10"
                  : "border-[color:var(--border-subtle)] hover:border-brand-teal/50 hover:bg-[color:var(--surface-2)] dark:border-white/10 dark:hover:bg-white/5")
              }
            >
              <span
                className={
                  "mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg " +
                  (active
                    ? "bg-brand-teal text-white"
                    : "bg-[color:var(--surface-2)] text-[color:var(--page-fg-secondary)] dark:bg-white/5 dark:text-slate-300")
                }
              >
                <Icon className="h-4 w-4" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium text-[color:var(--page-fg)]">
                  {label}
                </p>
                <p className="mt-0.5 text-[11px] text-[color:var(--page-fg-muted)]">{hint}</p>
              </div>
              {active && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-teal">
                  ACTIVE
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
